import type {MessageAttachment} from '@microsoft/agent-host-protocol'
import type {CopilotChatReference, FileDiffReference, FileReference} from '@github-ui/copilot-chat-types'

import {implicitReferenceToAttachment} from './implicit-context'

/** Picks the subset of an immersive `FileReference` the SDK diff side needs. */
function fileReferenceToSide(file: FileReference) {
  return {
    path: file.path,
    ref: file.ref,
    commitOID: file.commitOID,
    repoID: file.repoID,
    repoOwner: file.repoOwner,
    repoName: file.repoName,
  }
}

/**
 * Converts an immersive `FileDiffReference` — published by the shared diff-view
 * "Ask Copilot" / "Attach to thread" affordances (`@github-ui/copilot-code-chat`)
 * over `@github-ui/copilot-chat-events` — into the AHP `github_file_diff`
 * attachment quick chat submits. Reuses the implicit-context `file-diff` mapping
 * so the endpoint-driven and user-triggered paths build an identical SDK detail.
 */
export function fileDiffReferenceToAttachment(ref: FileDiffReference): MessageAttachment | undefined {
  return implicitReferenceToAttachment({
    type: 'file-diff',
    url: ref.url,
    baseFile: ref.baseFile ? fileReferenceToSide(ref.baseFile) : null,
    headFile: ref.headFile ? fileReferenceToSide(ref.headFile) : null,
  })
}

/**
 * Converts a `CopilotChatReference` carried on the shared chat events into an
 * AHP attachment. Explicit code-view and diff-view references share the same
 * mapping as references resolved through the implicit-context endpoint.
 */
export function copilotChatReferenceToAttachment(ref: CopilotChatReference): MessageAttachment | undefined {
  switch (ref.type) {
    case 'file':
    case 'file-v2':
    case 'snippet':
      return implicitReferenceToAttachment(ref)
    case 'file-diff':
      return fileDiffReferenceToAttachment(ref)
    default:
      return undefined
  }
}
